import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';
import Tooltip from '../../../tooltip';
import MathContent from '../../../math-content';
import { Badge, Container, Value } from '..';
import ErrorIcon from '../../assets/multiply.svg?react';
import SuccessIcon from '../../assets/checkmark.svg?react';

export const ErrorIconComponent = styled(ErrorIcon)`
    margin-left: 6px;
    width: 1.1em;

    fill: var(--color-error-default);
`;

export const SuccessIconComponent = styled(SuccessIcon)`
    margin-left: 6px;
    width: 1.2em;

    fill: var(--color-success-default);
`;

export const MissingIconComponent = styled(SuccessIcon)`
    margin-left: 6px;
    width: 1.2em;

    fill: var(--gray700);
`;

const CorrectAnswerTooltip = styled(Tooltip)`
    white-space: nowrap;
    color: var(--color-white);

    font-size: 12px;
    letter-spacing: 0.32px;
    line-height: 16px;
    top: 9px;
`;

export const ValidatedContainer = styled(Container)`
    border: 1px solid var(--gray200);
    background-color: var(--color-white);

    cursor: default;
    padding: 0;
    width: 100%;

    &:hover > .TooltipWrapper {
        display: block;
    }

    > .TooltipWrapper {
        display: none;
    }

    ${(props) =>
        props.type === 'error' &&
        css`
            border: 1px solid var(--color-error-default);
            background-color: var(--red50);
        `}

    ${(props) =>
        props.type === 'success' &&
        css`
            border: 1px solid var(--color-success-default);
            background-color: var(--green50);
        `}

    ${(props) =>
        props.type === 'warning' &&
        css`
            border: 1px solid var(--color-warning-default);
            background-color: var(--yellow50);
        `}

    ${(props) =>
        props.type === 'missing' &&
        css`
            border: 1px solid var(--gray700);
            background-color: var(--color-white);
        `}
`;

const ValueValidated = styled(Value)`
    ${(props) => props.hasBadgeContent && props.useIconsForValidation && 'padding-left: 0;'}
    font-size: 16px;
    min-height: 24px;
    letter-spacing: 0px;
    text-align: center;
    ${(props) =>
        props.hasBadgeContent &&
        css`
            margin-left: 32px;
        `}
`;

const ChoiceValidated = ({ badgeIndex, useIconsForValidation, id, scrollingFn, type, value }) => {
    const ref = useRef(null);
    const [mathRendered, setMathRendered] = useState(false);
    const onRendered = useCallback(() => setMathRendered(true), []);
    useEffect(() => {
        scrollingFn && scrollingFn(ref, mathRendered);
        return () => {};
    }, [scrollingFn, mathRendered]);

    const getIconByValidationType = () => {
        switch (type) {
            case 'error':
                return <ErrorIconComponent />;
            case 'success':
                return <SuccessIconComponent />;
            case 'missing':
                return <MissingIconComponent />;
            default:
                return null;
        }
    };

    const badgeContent = useIconsForValidation ? getIconByValidationType() : badgeIndex;

    return (
        <ValidatedContainer type={type} id={id} hasBadgeContent={!!badgeContent}>
            {badgeContent && (
                <Badge type={type} useIconsForValidation={useIconsForValidation}>
                    {badgeContent}
                </Badge>
            )}
            <ValueValidated
                ref={ref}
                onScroll={() => scrollingFn(ref)}
                hasBadgeContent={!!badgeContent}
                useIconsForValidation={useIconsForValidation}
            >
                <MathContent onRendered={onRendered}>{value}</MathContent>
            </ValueValidated>
            {type === 'missing' && <CorrectAnswerTooltip content="Questa è la risposta esatta" top="4px" />}
            {type === 'success' && <CorrectAnswerTooltip content="Risposta esatta" top="4px" />}
            {type === 'error' && <CorrectAnswerTooltip content="Risposta sbagliata" top="4px" />}
        </ValidatedContainer>
    );
};

ChoiceValidated.propTypes = {
    badgeIndex: PropTypes.string,
    useIconsForValidation: PropTypes.bool,
    id: PropTypes.string,
    scrollingFn: PropTypes.func,
    type: PropTypes.oneOf(['error', 'success', 'warning', 'missing']),
    value: PropTypes.string,
};

export default ChoiceValidated;
